import React from 'react'
import { ProgramsType, CurrencyType } from '../Shared/types'
import QtfSummary from './QtfSummary'
import MicromobilitySummary from './MicromobilitySummary'
import GrhSummary from './GrhSummary'
import FlexSummary from './FlexSummary'
import TransitPassSummary from './TransitPassSummary'
import CouponSummary from './CouponSummary'
import {
  grhType,
  qtfType,
  micromobilityType,
  flexType,
  transitPassType,
  couponType,
} from '../Shared/types'
import SummaryContainerBox from 'features/Programs/Summary/components/SummaryContainerBox'
import RolloutIcon from 'core-system/Icons/Actions/Rollout'
import styled from 'styled-components'
import palette from 'core-system/Themes/palette'
import Text from 'core-system/Text'
import { Month } from 'shared/DateUtils'
import { SegmentListType } from '../Shared/types'
import SegmentsDisplay from '../Shared/components/SegmentsDisplay'
import moment from 'moment'
import PillContainer from 'core-system/Pill/Pill'
import { getScopeSelectionLabel } from '../Overview/helperFunctions'

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.625rem;
`

const CaptionText = styled.div`
  ${(props) => props.theme.typography.action3};
  color: ${(props) => props.theme.palette.primary.pink800};
  text-transform: uppercase;
  letter-spacing: 0.7px;
  white-space: nowrap;
`

const DateContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

const CurrencyContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`

interface ProgramSummaryViewProps {
  selectedPrograms: ProgramsType[]
  grh: grhType
  qtf: qtfType
  micromobility: micromobilityType
  flex: flexType
  transitPass?: transitPassType
  coupon?: couponType
  selectedDate: Month
  monthlySpendDescription: string
  title: string
  estimatedMonthlySpend: number
  selectedSegments: SegmentListType[]
  cancelledSegments: SegmentListType[]
  displayType: 'table' | 'drawer'
  irsTaxLimit: number
  currency?: CurrencyType
}

const ProgramSummaryView = ({
  selectedPrograms,
  grh,
  qtf,
  micromobility,
  flex,
  transitPass,
  coupon,
  selectedDate,
  monthlySpendDescription,
  title,
  estimatedMonthlySpend,
  selectedSegments,
  cancelledSegments = [],
  displayType,
  irsTaxLimit,
  currency = 'USD',
}: ProgramSummaryViewProps) => {
  const firstBilledDate = () => {
    // If there is no qtf the first billed date is the same as the selected date
    if (!selectedPrograms.includes('qtf')) {
      return selectedDate.text
    }
    // If there is a qtf the first billed date is the next month
    const nextMonth = moment(selectedDate.text, 'MMMM D, YYYY').add(1, 'month')
    return nextMonth.format('MMMM D, YYYY')
  }

  const renderSummary = (program: ProgramsType) => {
    switch (program) {
      case 'qtf':
        return (
          <QtfSummary
            benefits={qtf.benefits}
            employerContributions={qtf.employerContributions}
            maxAllocationLimit={qtf.maxAllocationLimit}
            irsTaxLimit={irsTaxLimit}
          />
        )
      case 'grh':
        return (
          <GrhSummary
            transaction={grh.transaction}
            uses={grh.uses}
            activeOptions={grh.activeOptions}
            activeMerchantIds={grh.activeMerchantIds}
          />
        )
      case 'micromobility':
        return (
          <MicromobilitySummary
            activeOptions={micromobility.activeOptions}
            activeMerchantIds={micromobility.activeMerchantIds}
            budget={micromobility.budget}
            prorated={micromobility.prorated}
          />
        )
      case 'flex':
        return (
          <FlexSummary
            activeOptions={flex.activeOptions}
            activeMerchantIds={flex.activeMerchantIds}
            budget={flex.budget}
            budgetCycle={flex.budgetCycle}
            oneTimePurchase={flex.oneTimePurchase}
            rolloverBudget={flex.rolloverBudget}
            prorated={flex.prorated}
          />
        )
      case 'transitPass':
        if (!transitPass) return null
        return <TransitPassSummary transitPass={transitPass} />
      case 'coupon':
        if (!coupon) return null
        return <CouponSummary coupon={coupon} />
      default:
        return null
    }
  }

  if (selectedPrograms.length === 0) {
    return null
  }

  return (
    <>
      <Text variant='h5' marginBottom='1rem' color={palette.black}>
        {title}
      </Text>
      <Text variant='h2' marginBottom='1rem' color={palette.text.primary}>
        ${(estimatedMonthlySpend / 100).toFixed(2)}
      </Text>
      <Text
        variant='body1'
        marginBottom='1.5rem'
        color={palette.text.secondary}
      >
        {monthlySpendDescription}
      </Text>
      {cancelledSegments.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <Text variant='h5' marginBottom='1rem'>
            {getScopeSelectionLabel(cancelledSegments, true)}
          </Text>
          <SegmentsDisplay
            segments={cancelledSegments}
            displayType={displayType}
          />
        </div>
      )}
      <Text variant='h5' marginBottom='1rem'>
        {getScopeSelectionLabel(selectedSegments)}
      </Text>
      <SegmentsDisplay segments={selectedSegments} displayType={displayType} />
      <CurrencyContainer>
        <Text variant='h5' marginBottom='1rem'>
          Currency
        </Text>
        <PillContainer>{currency}</PillContainer>
      </CurrencyContainer>
      <Text
        variant='h5'
        marginBottom='1rem'
        marginTop='1.5rem'
        color={palette.black}
      >
        Timeline
      </Text>
      <DateContainer>
        <SummaryContainerBox>
          <Text
            variant='body1'
            marginBottom='0.5rem'
            color={palette.text.secondary}
          >
            Start Date
          </Text>
          <Text variant='action3' color={palette.text.primary}>
            {selectedDate?.text}
          </Text>
        </SummaryContainerBox>
        <SummaryContainerBox>
          <Text
            variant='body1'
            marginBottom='0.5rem'
            color={palette.text.secondary}
          >
            First Billed Date
          </Text>
          <Text variant='action3' color={palette.text.primary}>
            {firstBilledDate()}
          </Text>
        </SummaryContainerBox>
      </DateContainer>
      <Text
        variant='h5'
        marginTop='1.5rem'
        marginBottom='1rem'
        color={palette.black}
      >
        Configuration Summary
      </Text>
      {selectedPrograms.map((program: ProgramsType) => renderSummary(program))}
      <SummaryContainerBox>
        <HeaderContainer>
          <RolloutIcon color={palette.secondary.purple600} />
          <Text variant='action3' marginLeft='0.5rem'>
            Emails & Reminders
          </Text>
        </HeaderContainer>
        <CaptionText>Users</CaptionText>
      </SummaryContainerBox>
    </>
  )
}

export default React.memo(ProgramSummaryView)
